import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  ChevronDown,
  ChevronRight,
  BarChart3, 
  TrendingUp, 
  Calculator,
  FileText, 
  MessageSquare,
  Presentation,
  FileSearch,
  ClipboardList,
  FolderOpen,
  ExternalLink,
  User,
  Trash2,
  Sun,
  Bell
} from 'lucide-react';
import './ModernSidebar.css';
import config from '../config/api';

const ModernSidebar = ({ activePage, onPageChange, onLoadConversation }) => {
  const [expandedSections, setExpandedSections] = useState({
    recent: true,
    playground: true
  });
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Fetch conversations from API
  const fetchConversations = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = config.apiBaseUrl;
      const country = localStorage.getItem('selectedCountry');
      const headers = country ? { 'X-Country-Code': country } : {};
      const response = await fetch(`${API_BASE_URL}/api/chat/conversations`, {
        headers: headers
      });
      const data = await response.json();
      
      if (data.success) {
        setConversations(data.conversations || []);
      } else {
        console.error('Failed to fetch conversations:', data.error);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Delete conversation
  const deleteConversation = async (conversationId, event) => {
    event.stopPropagation(); // Prevent triggering conversation load
    
    try {
      const API_BASE_URL = config.apiBaseUrl;
      const country = localStorage.getItem('selectedCountry');
      const headers = country ? { 'X-Country-Code': country } : {};
      const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: headers
      });
      const data = await response.json();
      
      if (data.success) {
        // Remove from local state
        setConversations(prev => prev.filter(conv => conv.conversation_id !== conversationId));
      } else {
        console.error('Failed to delete conversation:', data.error);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  // Load conversation
  const loadConversation = async (conversationId) => {
    try {
      const API_BASE_URL = config.apiBaseUrl;
      const country = localStorage.getItem('selectedCountry');
      const headers = country ? { 'X-Country-Code': country } : {};
      const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${conversationId}`, {
        headers: headers
      });
      const data = await response.json();
      
      if (data.success && onLoadConversation) {
        // Convert timestamp strings to Date objects
        const messagesWithDateObjects = data.messages.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        
        onLoadConversation(conversationId, messagesWithDateObjects);
        onPageChange('chat');
      } else {
        console.error('Failed to load conversation:', data.error);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  // Format conversation title (first question)
  const formatConversationTitle = (lastQuery) => {
    if (!lastQuery) return 'Empty conversation';
    return lastQuery.length > 50 ? lastQuery.substring(0, 50) + '...' : lastQuery;
  };

  // Format conversation time
  const formatConversationTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return 'Today';
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    }
  };

  // Fetch conversations on component mount
  useEffect(() => {
    fetchConversations();
  }, []);

  // Expose refresh function to parent component
  useEffect(() => {
    if (onPageChange) {
      // Add refresh function to window for global access
      window.refreshSidebarConversations = fetchConversations;
    }
  }, [onPageChange]);

  const playgroundTools = [
    {
      id: 'board-deck-assistant',
      icon: ClipboardList,
      title: 'Board Deck Generator',
      page: 'board-deck'
    },
    {
      id: 'presentation-analysis',
      icon: Presentation,
      title: 'Presentation Analysis',
      page: 'presentation'
    },
    {
      id: 'transcript-analysis',
      icon: FileSearch,
      title: 'Transcript Analysis',
      page: 'transcript'
    },
    {
      id: 'variance-analysis',
      icon: TrendingUp,
      title: 'Variance Analysis',
      page: 'variance'
    },
    {
      id: 'file-manager',
      icon: FolderOpen,
      title: 'File Manager',
      page: 'file-manager'
    },
    {
      id: 'unstruct-ai',
      icon: ExternalLink,
      title: 'Unstruct AI',
      page: 'unstruct-ai'
    }
  ];

  const handlePageChange = (page) => {
    onPageChange(page);
  };

  return (
    <div className="modern-sidebar">
      {/* Header */}
      <div className="sidebar-header">
        <div className="logo">
          <img src="/assets/tngrm 1.JPG" alt="Logo" className="logo-image" />
          <span className="logo-text">Earnings Analyst</span>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="sidebar-main-content">
        {/* New Thread Button */}
        <div className="new-thread-section">
          <button 
            className="new-thread-btn"
            onClick={() => handlePageChange('chat')}
          >
            <Plus size={16} />
            <span>New Thread</span>
            <span className="shortcut">⌘K</span>
          </button>
        </div>

        {/* Recent Section */}
        <div className="sidebar-section">
          <div 
            className="section-header"
            onClick={() => toggleSection('recent')}
          >
            <span className="section-title">Recent</span>
            {expandedSections.recent ? (
              <ChevronDown size={12} />
            ) : (
              <ChevronRight size={12} />
            )}
          </div>
          
          {expandedSections.recent && (
            <div className="section-content">
              {loading ? (
                <div className="loading-conversations">Loading conversations...</div>
              ) : conversations.length === 0 ? (
                <div className="no-conversations">No conversations yet</div>
              ) : (
                conversations.map((conversation) => (
                  <div 
                    key={conversation.conversation_id} 
                    className="recent-item conversation-item"
                    onClick={() => loadConversation(conversation.conversation_id)}
                  >
                    <div className="conversation-content">
                      <div className="recent-title">
                        {formatConversationTitle(conversation.last_query)}
                      </div>
                      <div className="recent-time">
                        {formatConversationTime(conversation.timestamp)}
                      </div>
                    </div>
                    <button 
                      className="delete-conversation-btn"
                      onClick={(e) => deleteConversation(conversation.conversation_id, e)}
                      title="Delete conversation"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Playground Section */}
      <div className="sidebar-section playground-section">
        <div 
          className="section-header"
          onClick={() => toggleSection('playground')}
        >
          <span className="section-title">Playground</span>
          {expandedSections.playground ? (
            <ChevronDown size={12} />
          ) : (
            <ChevronRight size={12} />
          )}
        </div>
        
        {expandedSections.playground && (
          <div className="section-content">
            {playgroundTools.map((tool) => {
              const IconComponent = tool.icon;
              return (
                <div 
                  key={tool.id} 
                  className={`playground-item ${activePage === tool.page ? 'active' : ''}`}
                  onClick={() => handlePageChange(tool.page)}
                >
                  <IconComponent size={16} />
                  <span>{tool.title}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* User Section */}
      <div className="user-section">
        <div className="user-profile">
          <div className="user-avatar">
            <User size={16} />
          </div>
          <span className="user-name">User</span>
        </div>
        <div className="user-actions">
          <button className="action-btn">
            <Sun size={14} />
          </button>
          <button className="action-btn">
            <Bell size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModernSidebar;
